// src/components/proposals/proposalChrome.jsx
//
// The page furniture and layout primitives shared by every proposal-family
// document: the dark cover with its vignette stack, the header bar, the ghost
// page number, the footer, the section label, and the table-based replacements
// for CSS grid and space-between flex.
//
// WHY THIS FILE EXISTS. Bronze and Gold are per-tier independent documents
// (Option A), and each holds its own copy of all of the above — a change to the
// footer has to land twice or the two customer documents silently diverge. The
// Lead pre-proposal would have made three copies. It imports from here instead.
//
// ⚠️ BronzeTemplate.jsx and GoldTemplate.jsx still hold their own copies and
// have deliberately NOT been rewired in this change: they render live customer
// documents and a mechanical dedupe would need both re-tested against real
// PDFs. Everything below was copied from BronzeTemplate.jsx VERBATIM so that
// migration is a straight deletion when someone takes it on. If you change a
// value here, change it there too until then.
//
// LAYOUT IS TABLES, NOT CSS GRID. DocRaptor renders with Prince, which does not
// implement CSS grid and ignores flex `gap`. Every `display:grid` and every
// `justify-content:space-between` row in the source designs is a <table>.
import React from "react";

/* ══════════════════════════════════════════════════════════════════════
   Palette — SETUP.md "Colour Palette"

   ⚠ THE TEAL AND SLATE BELOW ARE MIRRORED IN THE BACKEND, and there is no way
   to share them. The quote document is rendered SERVER-SIDE by
   msp-os-backend/src/services/quoteRenderer.js — deliberately, so the PDF's
   totals cannot drift from the builder's — and a Node service cannot import a
   React module. Its QUOTE_CSS therefore repeats teal #0b80a5 / slate #2a4365
   for the quote cover page, and carries a note pointing back at this block.

   A second source of truth for STYLING ONLY is the accepted trade: a colour
   drifting between the two is a cosmetic bug, where a number drifting would be
   a wrong quote. Change the teal here and you must change it there.
   ══════════════════════════════════════════════════════════════════════ */
export const COLORS = {
  primary:   "#1a1a1a",
  teal:      "#0b80a5",
  slate:     "#2a4365",
  lightBg:   "#f8fafc",
  border:    "#e5e7eb",
  textMuted: "#6b7280",
  footer:    "#d1d5db",
};

/* ══════════════════════════════════════════════════════════════════════
   Tier accent

   Every component below takes an `accent` (and where it needs a second tone,
   `accentSoft`), defaulting to the brand teal so nothing that already calls
   these changes behaviour. This exists because Silver Guardian is not a teal
   document: its slate identity appears 113 times across its 10 pages, and
   hardcoding a hex in shared furniture would have meant a fourth copy of that
   furniture rather than one prop.

   `topBar` is the cover's gradient strip. It is a full CSS value, not a pair of
   stops, because Silver's is a THREE-stop gradient (slate → light slate → teal)
   where Bronze/Gold's is two. Encoding it as stops would have forced the odd
   one out into a shape that doesn't fit. */
export const TEAL_ACCENT = {
  accent:     "#0b80a5",
  accentSoft: "#0b80a5",
  topBar:     "linear-gradient(90deg, #0b80a5, #2a4365)",
};

export const PAGE_BASE = {
  width: 794, minHeight: 1123, background: "#ffffff",
  position: "relative", overflow: "hidden",
};

/* Height available to a page's content, once the header bar and footer are
   removed. Used as the minHeight of every content div so a short page still
   pushes its footer to the bottom of the sheet. */
export const PAGE_BODY_H = 1123 - 48 - 36;

/* ══════════════════════════════════════════════════════════════════════
   Table-layout primitives (Prince has no CSS grid)
   ══════════════════════════════════════════════════════════════════════ */

/* A full-width two-cell row: content left, value right. Replaces the
   source's `display:flex; justify-content:space-between`. */
export function SplitRow({ left, right, style, align = "center", rightStyle }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", ...style }}>
      <tbody>
        <tr>
          <td style={{ verticalAlign: align === "top" ? "top" : "middle", padding: 0 }}>{left}</td>
          <td style={{ verticalAlign: align === "top" ? "top" : "middle", padding: 0,
                       textAlign: "right", whiteSpace: "nowrap", width: "1%", ...rightStyle }}>
            {right}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

/* Replaces `display:grid; grid-template-columns:1fr 1fr; gap:<row> <col>`.
   Column gap comes from cell padding so the outer edges stay flush. */
export function TwoColTable({ items, renderItem, colGap = 10, rowGap = 10, style }) {
  const rows = [];
  for (let i = 0; i < items.length; i += 2) rows.push([items[i], items[i + 1]]);
  const half = colGap / 2;
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", ...style }}>
      <tbody>
        {rows.map((pair, r) => (
          <tr key={r}>
            {pair.map((item, ci) => (
              <td key={ci}
                  style={{ width: "50%", verticalAlign: "top", padding: 0,
                           paddingLeft:  ci === 0 ? 0 : half,
                           paddingRight: ci === 0 ? half : 0,
                           paddingBottom: r === rows.length - 1 ? 0 : rowGap }}>
                {item ? renderItem(item, r * 2 + ci) : null}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* Icon/bullet + text, as a two-cell row rather than a flex row. */
export function BulletRow({ bullet, children, gap = 9, bottom = 0, bulletWidth }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: bottom }}>
      <tbody>
        <tr>
          <td style={{ verticalAlign: "top", padding: 0, width: bulletWidth || 1,
                       paddingRight: gap, whiteSpace: "nowrap" }}>
            {bullet}
          </td>
          <td style={{ verticalAlign: "top", padding: 0 }}>{children}</td>
        </tr>
      </tbody>
    </table>
  );
}

export const dot = (color = COLORS.teal, size = 5, top = 6) => (
  <div style={{ width: size, height: size, borderRadius: "50%", background: color, marginTop: top }} />
);

/* ══════════════════════════════════════════════════════════════════════
   Editable field primitives
   edit  → input/textarea styled to inherit the surrounding typography
   view  → plain text
   print → plain text, no interactive chrome (REFINEMENTS.md §5 pattern B)
   ══════════════════════════════════════════════════════════════════════ */
const inheritInput = {
  background: "rgba(11,128,165,0.06)",
  border: "1px dashed rgba(11,128,165,0.35)",
  borderRadius: 3,
  outline: "none",
  width: "100%",
  padding: "1px 4px",
  margin: 0,
  display: "block",
  fontFamily: "inherit",
  fontSize: "inherit",
  fontWeight: "inherit",
  fontStyle: "inherit",
  color: "inherit",
  letterSpacing: "inherit",
  lineHeight: "inherit",
  textAlign: "inherit",
};

/* An input that hugs its own text.

   Only ever mounts in EDIT mode — F returns a plain <span> for view/print — so
   the DOM measurement here can never reach the DocRaptor render path. That
   matters: renderToString produces a static string with no layout pass, so
   anything measured at runtime would silently not apply to the PDF. */
function AutoWidthInput({ value, onChange, placeholder, style, className }) {
  const measureRef = React.useRef(null);
  const [textWidth, setTextWidth] = React.useState(null);

  React.useLayoutEffect(() => {
    const el = measureRef.current;
    if (el) setTextWidth(Math.ceil(el.getBoundingClientRect().width));
  }, [value, placeholder, style.fontSize, style.fontWeight, style.letterSpacing]);

  const PAD = 5;   // 2px padding each side + border; keeps punctuation tight

  return (
    <>
      <span ref={measureRef} aria-hidden="true"
            style={{ ...style, position: "absolute", top: -9999, left: -9999,
                     visibility: "hidden", whiteSpace: "pre", pointerEvents: "none",
                     border: 0, padding: 0, width: "auto" }}>
        {value || placeholder || ""}
      </span>
      <input
        type="text" className={className} value={value || ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...style,
                 width: textWidth == null ? "auto" : textWidth + PAD,
                 padding: "1px 2px", display: "inline-block", verticalAlign: "baseline" }}
      />
    </>
  );
}

/* Inline text field. `style` is the typography of the text it replaces. */
export function F({ mode, value, onChange, style, placeholder, multiline, rows = 3, inline }) {
  if (mode !== "edit") {
    return <span style={style}>{value}</span>;
  }
  if (multiline) {
    return (
      <textarea
        className="no-print"
        rows={rows}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inheritInput, ...style, resize: "vertical" }}
      />
    );
  }
  if (inline) {
    return (
      <AutoWidthInput className="no-print" value={value} placeholder={placeholder}
                      onChange={onChange} style={{ ...inheritInput, ...style }} />
    );
  }
  return (
    <input
      type="text"
      className="no-print"
      value={value || ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      // Caller style spread LAST: inheritInput is a default, not an override.
      style={{ ...inheritInput, ...style }}
    />
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Page chrome — header bar, ghost number, footer, section label
   ══════════════════════════════════════════════════════════════════════ */

/* `docLabel` names the document in the top-right of every page. It defaults to
   "Technology Proposal" so Bronze/Gold read unchanged if they ever migrate
   here; the Lead passes its own, because a pre-proposal that calls itself a
   proposal is the first thing a prospect would query. */
export function HeaderBar({ customerName, docLabel = "Technology Proposal",
                            accent = TEAL_ACCENT.accentSoft }) {
  return (
    <div style={{ height: 48, background: "#1a1a1a", padding: "0 64px" }}>
      <table style={{ width: "100%", height: 48, borderCollapse: "collapse" }}>
        <tbody>
          <tr>
            <td style={{ verticalAlign: "middle", padding: 0 }}>
              <span style={{ color: accent, fontSize: 10, fontWeight: 700,
                             letterSpacing: "0.18em", textTransform: "uppercase" }}>
                DATATECH SOLUTIONS
              </span>
            </td>
            <td style={{ verticalAlign: "middle", padding: 0, textAlign: "right" }}>
              <span style={{ color: "rgba(255,255,255,0.38)", fontSize: 10,
                             letterSpacing: "0.06em", textTransform: "uppercase" }}>
                {customerName} · {docLabel}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function GhostNumber({ n }) {
  return (
    <div style={{ position: "absolute", top: 44, right: 58, fontSize: 160, fontWeight: 900,
                  color: "rgba(0,0,0,0.028)", lineHeight: 1, userSelect: "none",
                  pointerEvents: "none", letterSpacing: "-0.05em" }}>
      {n}
    </div>
  );
}

/* REFINEMENTS.md "Footer anchoring": absolute, direct child of .page,
   white background, z-index 2 so long content can't overlap it. */
export function PageFooter() {
  return (
    <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 36,
                  borderTop: "1px solid #f0f0f0", background: "#ffffff",
                  padding: "0 64px", zIndex: 2 }}>
      <table style={{ width: "100%", height: 36, borderCollapse: "collapse" }}>
        <tbody>
          <tr>
            <td style={{ verticalAlign: "middle", padding: 0 }}>
              <span style={{ fontSize: 10, color: "#9ca3af", letterSpacing: "0.04em" }}>
                Confidential — Not for Distribution
              </span>
            </td>
            <td style={{ verticalAlign: "middle", padding: 0, textAlign: "right" }}>
              <span style={{ fontSize: 10, color: "#9ca3af", letterSpacing: "0.04em" }}>
                datatech.net.au
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function SectionLabel({ text, accent = TEAL_ACCENT.accentSoft }) {
  return (
    <table style={{ borderCollapse: "collapse", marginBottom: 8 }}>
      <tbody>
        <tr>
          <td style={{ verticalAlign: "middle", padding: 0, paddingRight: 12 }}>
            <div style={{ width: 30, height: 2, background: accent }} />
          </td>
          <td style={{ verticalAlign: "middle", padding: 0 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: accent,
                           letterSpacing: "0.16em", textTransform: "uppercase" }}>
              {text}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

/* A dark band naming a group of rows inside a table-based section.
   `action` hosts a screen-only control (the Investment page's "+ Add Row"), and
   tightens the padding to keep the band's height where it was — those heights
   are budgeted by the page packer, so a taller band silently under-fills. */
export function SectionBand({ title, hint, extraStyle, action, accent = TEAL_ACCENT.accent }) {
  const body = (
    <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.14em", color: "#ffffff",
                textTransform: "uppercase", margin: 0 }}>
      {title}
      {hint && <span style={{ color: "rgba(255,255,255,0.5)", fontWeight: 600 }}> {hint}</span>}
    </p>
  );
  return (
    <div style={{ padding: action ? "6px 20px" : "7px 20px", background: "#1f2937",
                  borderLeft: `4px solid ${accent}`, ...extraStyle }}>
      {action ? <SplitRow left={body} right={action} /> : body}
    </div>
  );
}

/* Raw SVG inner-markup from the source's icon arrays. */
export function Icon({ paths, size = 14, stroke = TEAL_ACCENT.accent, width = 2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke}
         strokeWidth={width} strokeLinecap="round" strokeLinejoin="round"
         dangerouslySetInnerHTML={{ __html: paths }} />
  );
}

/* Number cell that becomes a bold plain value in print — REFINEMENTS.md §5
   pattern B, kept exactly (input.no-print beside span.print-only). */
/* ── Money entry inside a document ────────────────────────────────────────
   Deliberately NOT <input type="number">:
     - no spinner arrows
     - starts genuinely empty rather than at a literal 0, which is what made
       typing "10" into a zeroed field read "010"
     - holds the RAW STRING while focused so formatting cannot fight the user
       mid-keystroke, and settles to 2dp only on blur
     - shows a $ prefix and the committed value as $x.xx once you leave it

   The sibling of DecimalInput in FoundationalEditor.jsx, which does the same
   job for the app-shell forms and is styled by the `inp` class. This one is
   inline-styled because it renders inside the document itself, where that
   stylesheet does not apply. If the two ever need to diverge in BEHAVIOUR
   rather than styling, that is a bug in one of them. */
/* ⚠ `className` IS OPTIONAL AND MUST STAY OPTIONAL. This renders inside the
   PROPOSAL DOCUMENT, where the app stylesheet does not apply and the field is
   styled entirely by `inputStyle` — so a hardcoded `.inp` here would put the
   app's chrome (its border, radius, background and 12px face) onto a printed
   customer document.

   It is also reused in the APP SHELL by the quote builder, where the opposite
   is true: with no class the input falls back to the browser's own default
   width — measured at 143px against a 90px column — and renders with a 2px
   inset border and a white ground, overflowing its cell by 79px and disappearing
   under the columns beside it. That caller passes "inp"; the document does not. */
export function MoneyInput({ value, onCommit, placeholder = "0.00", ariaLabel,
                             inputStyle, className, invalid = false,
                             prefix = "$", decimals = 2 }) {
  const [draft, setDraft] = React.useState(null);   // non-null only while editing
  const n = value === "" || value == null ? NaN : Number(value);
  /* decimals:null shows the number as it is — "6" and "2.5" — which is what a
     QUANTITY needs. Money keeps 2dp. */
  const fmt = (x) => (decimals === null ? String(x) : x.toFixed(decimals));
  const shown = draft !== null ? draft : (Number.isFinite(n) ? fmt(n) : "");

  const sanitise = (raw) => {
    let s = String(raw).replace(/[^0-9.]/g, "");
    const first = s.indexOf(".");
    if (first !== -1) {                              // keep only the first dot
      s = s.slice(0, first + 1) + s.slice(first + 1).replace(/\./g, "");
    }
    return s;
  };

  const commit = () => {
    const s = draft === null ? shown : draft;
    setDraft(null);
    if (s === "" || s === ".") return onCommit("");
    const parsed = parseFloat(s);
    onCommit(Number.isFinite(parsed) ? Number(parsed.toFixed(decimals === null ? 2 : decimals)) : "");
  };

  return (
    <span style={{ position: "relative", display: "block" }}>
      {/* ⚠ OPTIONAL. A quantity is not money — it takes no $ and no room for
          one. Defaulting to "$" keeps every existing caller unchanged. */}
      {prefix ? (
        <span style={{ position: "absolute", left: 7, top: "50%", transform: "translateY(-50%)",
                       fontSize: 11, color: "#9ca3af", pointerEvents: "none" }}>{prefix}</span>
      ) : null}
      <input
        type="text" inputMode="decimal" aria-label={ariaLabel} placeholder={placeholder}
        className={className}
        value={shown}
        onChange={(e) => setDraft(sanitise(e.target.value))}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        style={{ ...inputStyle, textAlign: "right",
                 ...(prefix ? { paddingLeft: 16 } : null),
                 borderColor: invalid ? "#ef4444" : (inputStyle && inputStyle.borderColor) }}
      />
    </span>
  );
}

export function NumberCell({ mode, value, onChange, min = 1, max = 500 }) {
  if (mode === "print") {
    return <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a" }}>{value}</span>;
  }
  const disabled = mode === "view";
  return (
    <>
      <input
        type="number" min={min} max={max} value={value} disabled={disabled}
        onChange={(e) => onChange(Math.max(min, parseInt(e.target.value, 10) || min))}
        className="no-print"
        style={{ width: 52, padding: "3px 6px", border: "1px solid #e5e7eb", borderRadius: 4,
                 fontSize: 12, textAlign: "center", fontFamily: "inherit", color: "#1a1a1a" }}
      />
      <span className="print-only" style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a" }}>{value}</span>
    </>
  );
}

/* Tick box matching the source's 20px square checkbox. */
export function CheckBox({ checked, onToggle, mode, accent = TEAL_ACCENT.accent }) {
  if (mode === "print") return null;
  const common = {
    width: 20, height: 20, borderRadius: 4, cursor: mode === "edit" ? "pointer" : "default",
    display: "inline-block", verticalAlign: "middle",
  };
  const handler = mode === "edit" ? onToggle : undefined;
  if (!checked) {
    return <div className="no-print" onClick={handler}
                style={{ ...common, border: "2px solid #d1d5db", background: "#ffffff" }} />;
  }
  return (
    <div className="no-print" onClick={handler}
         style={{ ...common, border: `2px solid ${accent}`, background: accent,
                  textAlign: "center", lineHeight: "16px" }}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff"
           strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </div>
  );
}

/* Small "+ Add" / "✕" controls, hidden in print. */
export function AddButton({ label, onClick, disabled, accent = TEAL_ACCENT.accent }) {
  return (
    <div className="no-print" onClick={disabled ? undefined : onClick}
         style={{ marginTop: 10, padding: "7px 0", textAlign: "center",
                  cursor: disabled ? "default" : "pointer",
                  background: disabled ? "rgba(0,0,0,0.02)" : "rgba(0,0,0,0.04)",
                  border: `1px dashed ${accent}`, borderRadius: 6,
                  fontSize: 10, fontWeight: 700, color: accent,
                  letterSpacing: "0.08em", textTransform: "uppercase",
                  opacity: disabled ? 0.45 : 1 }}>
      {label}
    </div>
  );
}

export function DeleteButton({ onClick }) {
  return (
    <div className="no-print" onClick={onClick}
         style={{ width: 20, height: 20, textAlign: "center", lineHeight: "20px",
                  cursor: "pointer", background: "rgba(239,68,68,0.1)", borderRadius: 4 }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#ef4444"
           strokeWidth="2.5" strokeLinecap="round">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   PAGE 1 — Cover
   Vertical rhythm via a full-height 3-row table (top logo / centred title /
   bottom metadata) instead of a flex column: same visual result, and table
   vertical-align is the one thing Prince is reliably good at.
   ══════════════════════════════════════════════════════════════════════ */
const coverKicker = {
  color: "rgba(255,255,255,0.38)", fontSize: 10, fontWeight: 600,
  letterSpacing: "0.14em", textTransform: "uppercase", margin: "0 0 7px",
};

export function CoverPage({ c, mode, set, assetBase, onCoverImage,
                            kicker = "Technology Success Proposal", subtitle,
                            accent = TEAL_ACCENT.accentSoft,
                            topBar = TEAL_ACCENT.topBar }) {
  const isEdit = mode === "edit";
  return (
    <div className="page" style={{ ...PAGE_BASE, background: "#1a1a1a" }}>
      {c.coverImage ? (
        <img src={c.coverImage} alt=""
             style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
                      objectFit: "cover", objectPosition: "center 38%", display: "block" }} />
      ) : mode === "print" ? null : (
        <div className="no-print"
             style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                      border: "2px dashed rgba(255,255,255,0.18)",
                      color: "rgba(255,255,255,0.35)", fontSize: 13, textAlign: "center",
                      paddingTop: 460 }}>
          Drop a customer photo here — or upload via the settings panel
        </div>
      )}

      {/* Tonal + vignette stack, verbatim */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                    background: "rgba(12,12,12,0.28)", zIndex: 1 }} />
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1,
                    background: "linear-gradient(to right, rgba(10,10,10,0.82) 0%, rgba(10,10,10,0.55) 38%, rgba(10,10,10,0.0) 72%)" }} />
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1,
                    background: "linear-gradient(to top, rgba(10,10,10,0.90) 0%, rgba(10,10,10,0.40) 18%, rgba(10,10,10,0.0) 38%)" }} />
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1,
                    background: "linear-gradient(to bottom, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.0) 18%)" }} />
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 5, zIndex: 3,
                    background: topBar }} />

      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 2,
                    padding: "56px 68px 52px" }}>
        <table style={{ width: "100%", height: "100%", borderCollapse: "collapse" }}>
          <tbody>
            <tr>
              <td style={{ height: 1, verticalAlign: "top", padding: 0 }}>
                <img src={`${assetBase}/proposal-assets/logo-transparent.png`} alt="Datatech Solutions"
                     style={{ height: 56, objectFit: "contain", objectPosition: "left center" }} />
              </td>
            </tr>

            <tr>
              <td style={{ verticalAlign: "middle", padding: "56px 0 44px" }}>
                <table style={{ borderCollapse: "collapse", marginBottom: 22 }}>
                  <tbody>
                    <tr>
                      <td style={{ verticalAlign: "middle", padding: 0, paddingRight: 14 }}>
                        <div style={{ width: 44, height: 2, background: accent }} />
                      </td>
                      <td style={{ verticalAlign: "middle", padding: 0 }}>
                        <span style={{ color: accent, fontSize: 11, fontWeight: 700,
                                       letterSpacing: "0.2em", textTransform: "uppercase" }}>
                          {kicker}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* 46px, not the original 62px, and a wider column: real business
                    names ("Holiday Inn Cockburn") clipped mid-word at 62/540.
                    A FIXED size deliberately — per-name scaling would have to be
                    measured in the DOM, and mode="print" is rendered by
                    renderToString with no layout pass, so the PDF would silently
                    disagree with the preview. */}
                <h1 style={{ color: "#ffffff", fontSize: 46, fontWeight: 800, lineHeight: 1.06,
                             margin: "0 0 26px", letterSpacing: "-0.03em", maxWidth: "100%" }}>
                  <F mode={mode} value={c.customerName} onChange={(v) => set({ customerName: v })}
                     style={{ color: "#ffffff", fontSize: 46, fontWeight: 800, lineHeight: 1.06,
                              letterSpacing: "-0.03em" }} />
                </h1>
                <p style={{ color: "#ffffffc8", fontSize: 16, lineHeight: 1.7, maxWidth: 480,
                            margin: 0, fontWeight: 300 }}>
                  {subtitle || (
                    <>A tailored technology strategy designed to strengthen your IT foundation,
                    protect your operations, and support your long-term success.</>
                  )}
                </p>
              </td>
            </tr>

            <tr>
              <td style={{ height: 1, verticalAlign: "bottom", padding: 0 }}>
                <div style={{ borderTop: "1px solid rgba(255,255,255,0.13)", paddingTop: 32 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <tbody>
                      <tr>
                        <td style={{ verticalAlign: "bottom", padding: 0 }}>
                          <p style={coverKicker}>Prepared For</p>
                          <p style={{ color: "#ffffff", fontSize: 15, fontWeight: 700,
                                      margin: "0 0 3px", letterSpacing: "-0.01em" }}>
                            <F mode={mode} value={c.customerFullName}
                               onChange={(v) => set({ customerFullName: v })}
                               style={{ color: "#ffffff", fontSize: 15, fontWeight: 700 }} />
                          </p>
                          <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 12,
                                      fontWeight: 300, margin: 0, letterSpacing: "0.01em" }}>
                            {c.customerName}
                          </p>
                        </td>
                        <td style={{ verticalAlign: "bottom", padding: 0, textAlign: "center" }}>
                          <p style={coverKicker}>Prepared By</p>
                          <p style={{ color: "#ffffff", fontSize: 15, fontWeight: 600, margin: 0,
                                      letterSpacing: "-0.01em" }}>
                            <F mode={mode} value={c.salesRepName} onChange={(v) => set({ salesRepName: v })}
                               style={{ color: "#ffffff", fontSize: 15, fontWeight: 600, textAlign: "center" }} />
                          </p>
                          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 12,
                                      margin: "4px 0 0", fontWeight: 300 }}>
                            <F mode={mode} value={c.salesRepTitle} onChange={(v) => set({ salesRepTitle: v })}
                               style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, fontWeight: 300, textAlign: "center" }} />
                          </p>
                        </td>
                        <td style={{ verticalAlign: "bottom", padding: 0, textAlign: "right" }}>
                          <p style={coverKicker}>Date</p>
                          <p style={{ color: "#ffffff", fontSize: 15, fontWeight: 600, margin: 0 }}>
                            <F mode={mode} value={c.proposalDate} onChange={(v) => set({ proposalDate: v })}
                               style={{ color: "#ffffff", fontSize: 15, fontWeight: 600, textAlign: "right" }} />
                          </p>
                          <p style={{ color: "rgba(255,255,255,0.38)", fontSize: 11, margin: "4px 0 0" }}>
                            Confidential
                          </p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Cover uploads never go through `set` — the image must not land in
          `content`, which snapshots deep-clone. onCoverImage routes it to the
          shell's deduplicated store. No handler, no control. */}
      {isEdit && onCoverImage && (
        <div className="no-print"
             style={{ position: "absolute", top: 12, right: 12, zIndex: 5 }}>
          <label style={{ display: "inline-block", padding: "6px 12px", cursor: "pointer",
                          background: "rgba(11,128,165,0.9)", color: "#fff", borderRadius: 6,
                          fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                          textTransform: "uppercase" }}>
            {c.coverImage ? "Change cover" : "Upload cover"}
            <input type="file" accept="image/*" style={{ display: "none" }}
                   onChange={(e) => readCoverImage(e, (dataUrl) => onCoverImage(dataUrl))} />
          </label>
          {c.coverImage && (
            <span onClick={() => onCoverImage(null)}
                  style={{ display: "inline-block", marginLeft: 6, padding: "6px 10px",
                           cursor: "pointer", background: "rgba(239,68,68,0.9)", color: "#fff",
                           borderRadius: 6, fontSize: 10, fontWeight: 700,
                           letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Remove
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* Client-side compress to JPEG before storing — SETUP.md "Cover Image
   Upload". Keeps the state blob (and the PDF) a sane size. */
export function readCoverImage(event, done) {
  const file = event.target.files && event.target.files[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (ev) => {
    const img = new Image();
    img.onload = () => {
      const MAX = 1600;
      const scale = Math.min(1, MAX / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      done(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.src = ev.target.result;
  };
  reader.readAsDataURL(file);
}

/* Placeholder shown where an editable list is empty. Never reaches a customer
   PDF with content in it — an empty section is either toggled off or shows
   this, and this is what tells the author which of the two happened. */
export function EmptyBlock({ title, hint, pad = 24 }) {
  return (
    <div style={{ border: "2px dashed #e5e7eb", borderRadius: 10, padding: pad,
                  textAlign: "center", color: "#6b7280" }}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#d1d5db"
           strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
           style={{ margin: "0 auto 8px", display: "block" }}>
        <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="16" />
        <line x1="8" y1="12" x2="16" y2="12" />
      </svg>
      <p style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", margin: "0 0 3px" }}>{title}</p>
      <p style={{ fontSize: 11, color: "#9ca3af", margin: 0 }}>{hint}</p>
    </div>
  );
}
